/**
 * ApprovedVendorList — /suppliers/avl (Supplier S1).
 *
 * The Approved Vendor List as a material ↔ supplier matrix: rows are materials,
 * columns are the approved suppliers, cells carry the approval status (Approved /
 * Conditional / Disqualified / —). Single-source materials are flagged (a key
 * supply-continuity risk), and the summary surfaces conditional pairs +
 * qualifications expiring within 90 days.
 */
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { PageHero, StatCard, Stagger, Skeleton, EmptyState, Button } from '@edms/design-system';
import { useAvlQuery, type AvlStatus } from './useAvlQuery';
import styles from './ApprovedVendorList.module.css';

const STATUS_SYMBOL: Record<AvlStatus, string> = {
  APPROVED: '✓',
  CONDITIONAL: '!',
  DISQUALIFIED: '✕',
};
const STATUS_CLASS: Record<AvlStatus, string> = {
  APPROVED: 'cellApproved',
  CONDITIONAL: 'cellConditional',
  DISQUALIFIED: 'cellDisqualified',
};

export function ApprovedVendorList() {
  const { t } = useTranslation();
  const q = useAvlQuery();
  const data = q.data;

  // Index the matrix by material_id → supplier_id → cell for O(1) lookup.
  const cellIndex = useMemo(() => {
    const m = new Map<string, Map<string, { status: AvlStatus; is_primary: boolean; qualified_until: string | null }>>();
    for (const c of data?.matrix ?? []) {
      if (!m.has(c.material_id)) m.set(c.material_id, new Map());
      m.get(c.material_id)!.set(c.supplier_id, { status: c.status, is_primary: c.is_primary, qualified_until: c.qualified_until });
    }
    return m;
  }, [data]);

  return (
    <div className={styles.screen}>
      <PageHero
        eyebrow={t('avl.eyebrow', { defaultValue: 'Suppliers' })}
        title={t('avl.title', { defaultValue: 'Approved Vendor List' })}
        subtitle={t('avl.subtitle', { defaultValue: 'Which approved suppliers may supply which material — with single-source and qualification-expiry risk surfaced at a glance.' })}
      />

      {q.isLoading && (
        <>
          <div className={styles.kpiStrip} aria-hidden="true">{[0, 1, 2, 3].map((i) => <Skeleton key={i} height={104} radius={14} />)}</div>
          <Skeleton height={320} radius={14} />
        </>
      )}
      {q.isError && (
        <div className={styles.errorBanner} role="alert">
          <span>{t('avl.error', { defaultValue: 'Could not load the Approved Vendor List.' })}</span>
          <Button variant="secondary" size="sm" onClick={() => q.refetch()}>{t('common.retry', { defaultValue: 'Retry' })}</Button>
        </div>
      )}

      {data && (
        <>
          <Stagger className={styles.kpiStrip} step={50}>
            <StatCard label={t('avl.kpi.materials', { defaultValue: 'Materials' })} value={data.summary.materials} accent="var(--spectro-purple, #9333EA)" deltaCaption={t('avl.kpi.suppliers', { defaultValue: '{{n}} approved suppliers', n: data.summary.suppliers })} />
            <StatCard label={t('avl.kpi.approved', { defaultValue: 'Approved pairs' })} value={data.summary.approved_pairs} accent="var(--color-status-success, #16A34A)" />
            <StatCard label={t('avl.kpi.single', { defaultValue: 'Single-source materials' })} value={data.summary.single_source_materials} accent={data.summary.single_source_materials > 0 ? 'var(--color-status-error, #DC2626)' : 'var(--color-status-success, #16A34A)'} deltaCaption={t('avl.kpi.singleHint', { defaultValue: 'supply-continuity risk' })} />
            <StatCard label={t('avl.kpi.expiring', { defaultValue: 'Quals expiring ≤90d' })} value={data.summary.expiring_90d} accent={data.summary.expiring_90d > 0 ? 'var(--color-status-warning, #D97706)' : 'var(--color-status-success, #16A34A)'} />
          </Stagger>

          <div className={styles.legend} aria-hidden="true">
            <span className={`${styles.legendChip} ${styles.cellApproved}`}>✓ {t('avl.legend.approved', { defaultValue: 'Approved' })}</span>
            <span className={`${styles.legendChip} ${styles.cellConditional}`}>! {t('avl.legend.conditional', { defaultValue: 'Conditional' })}</span>
            <span className={`${styles.legendChip} ${styles.cellDisqualified}`}>✕ {t('avl.legend.disqualified', { defaultValue: 'Disqualified' })}</span>
            <span className={styles.legendChip}>— {t('avl.legend.none', { defaultValue: 'Not approved' })}</span>
            <span className={styles.legendStar}>★ {t('avl.legend.primary', { defaultValue: 'Primary source' })}</span>
          </div>

          {data.materials.length === 0 ? (
            <EmptyState icon="🏭" title={t('avl.emptyTitle', { defaultValue: 'No materials defined' })} description={t('avl.empty', { defaultValue: 'Add materials and approve suppliers to populate the AVL.' })} />
          ) : (
            <div className={styles.tableScroll}>
              <table className={styles.matrix}>
                <thead>
                  <tr>
                    <th className={styles.cornerCell}>{t('avl.material', { defaultValue: 'Material' })}</th>
                    {data.suppliers.map((s) => (
                      <th key={s.supplier_id} className={styles.supCol}>
                        <Link to="/suppliers/$id" params={{ id: s.supplier_id }} className={styles.supLink} title={s.name}>
                          {s.name}
                        </Link>
                        <span className={styles.supSub}>{s.subtype?.replace(/_/g, ' ').toLowerCase()}</span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.materials.map((mat) => (
                    <tr key={mat.material_id} className={mat.single_source ? styles.singleRow : ''}>
                      <th scope="row" className={styles.matCell}>
                        <span className={styles.matName}>{mat.name}</span>
                        <span className={styles.matMeta}>
                          {mat.category.replace(/_/g, ' ').toLowerCase()} · {mat.criticality.toLowerCase()}
                          {mat.single_source && <span className={styles.singleBadge}>single-source</span>}
                        </span>
                      </th>
                      {data.suppliers.map((s) => {
                        const cell = cellIndex.get(mat.material_id)?.get(s.supplier_id);
                        if (!cell) return <td key={s.supplier_id} className={styles.cellNone}>—</td>;
                        return (
                          <td key={s.supplier_id} className={styles[STATUS_CLASS[cell.status]]} title={cell.qualified_until ? `Qualified until ${new Date(cell.qualified_until).toLocaleDateString()}` : cell.status}>
                            <span className={styles.cellSymbol}>{STATUS_SYMBOL[cell.status]}</span>
                            {cell.is_primary && <span className={styles.primaryStar} aria-label="primary source">★</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <footer className={styles.disclaimer}>
            {t('avl.disclaimer', { defaultValue: 'Cell status derives from each supplier’s lifecycle + risk (Conditional = open CAPA / requal due / critical risk). Single-source materials carry supply-continuity risk — qualify a second source where feasible. Advisory; the authoritative AVL is the approved-materials register.' })}
          </footer>
        </>
      )}
    </div>
  );
}
